import React, { useCallback, useEffect, useState } from "react";
import OverviewNavigation from "../OverviewNavigation/OverviewNavigation";
import { useLocation, useNavigate } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { SortingTable } from "../Tables";
import AddExamDialog from "./Dialogs/AddExamDialog";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { ExamOverviewRow, ExamInterface } from "../../interfaces/examInterfaces";
import { RootState } from "../..";
import AddFolderDialog from "./Dialogs/AddFolderDialog";
import firebaseExamService, { loadExam } from "../../firebase/firebaseExamService";
import DeleteExamDialog from "./Dialogs/DeleteExamDialog";
import { DIALOGS, DIALOG_TYPES } from "../Dialogs/dialogReducer";
import DeleteFolderDialog from "./Dialogs/DeleteFolderDialog";
import RenameExamDialog from "./Dialogs/RenameExamDialog";
import RenameFolderDialog from "./Dialogs/RenameFolderDialog";
import MoveExamDialog from "./Dialogs/MoveExamDialog";
import MoveFolderDialog from "./Dialogs/MoveFolderDialog";
import CopyExamDialog from "./Dialogs/CopyExamDialog";
import MainCustomContainer from "../Containers/MainCustomContainer";
import { Grade } from "../../types/App";
import ExamExistsDialog from "./Dialogs/ExamExistsDialog";
import { handleOpenDialog } from "../ExamBuilder/Functions/FunctionsMain";
import FolderExistsDialog from "./Dialogs/FolderExistsDialog";

const ExamOverview: React.FC = () => {
  const location = useLocation();
  const paths = useSelector((state: RootState) => state.examOverview.paths);
  const examAccess = useSelector((state: RootState) => state.examOverview.examAccess);
  const pathString = decodeURI(location.pathname.replace("/exam-overview", "")) || "/";
  const user = useSelector((state: RootState) => state.app.user);
  const userId = user?.uid;
  const [rows, setRows] = useState<ExamOverviewRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<ExamOverviewRow | null>(null);
  const [targetPath, setTargetPath] = useState<string>("");
  const dispatch = useDispatch();
  const dialogDispatch = useDispatch();
  const navigate = useNavigate();

  const addExamDialogOpen = useSelector((state: RootState) => state.dialogs.addExamDialog);
  const addFolderDialogOpen = useSelector((state: RootState) => state.dialogs.addFolderDialog);
  const renameExamDialogOpen = useSelector((state: RootState) => state.dialogs.renameExamDialog);
  const renameFolderDialogOpen = useSelector((state: RootState) => state.dialogs.renameFolderDialog);
  const copyExamDialogOpen = useSelector((state: RootState) => state.dialogs.copyExamDialog);
  const moveExamDialogOpen = useSelector((state: RootState) => state.dialogs.moveExamDialog);
  const examExistsDialogOpen = useSelector((state: RootState) => state.dialogs.examExistsDialog);
  const moveFolderDialogOpen = useSelector((state: RootState) => state.dialogs.moveFolderDialog);
  const folderExistsDialogOpen = useSelector((state: RootState) => state.dialogs.folderExistsDialog);
  const deleteExamDialogOpen = useSelector((state: RootState) => state.dialogs.deleteExamDialog);
  const deleteFolderDialogOpen = useSelector((state: RootState) => state.dialogs.deleteFolderDialog);

  const validGrades = examAccess
    ? Object.keys(examAccess)
        .filter((key) => {
          const fromDate = new Date(
            examAccess[key].validFrom.seconds * 1000 + examAccess[key].validFrom.nanoseconds / 1000000
          );
          const toDate = new Date(
            examAccess[key].validTo.seconds * 1000 + examAccess[key].validTo.nanoseconds / 1000000
          );
          const now = new Date();
          return fromDate < now && toDate > now;
        })
        .map((grade) => +grade)
    : null;

  const subPaths = useCallback(() => {
    if (userId) {
      firebaseExamService.subscribeToUserDoc({ userId, dispatch });
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (location.state?.selectedRow) {
      setSelectedRow(location.state.selectedRow);
    }
  }, [location.state?.selectedRow]);

  useEffect(() => {
    return subPaths();
  }, [subPaths]);

  useEffect(() => {
    if (paths === null || Object.keys(paths).length === 0) {
      setRows([]);
    } else {
      const selectedPath = paths[pathString];
      const rowsData: ExamOverviewRow[] = selectedPath.map((path: ExamOverviewRow) => ({
        id: path.id,
        isDirectory: path.isDirectory,
        name: path.name,
        showOptions: false,
        grade: path.grade,
      }));
      setRows(rowsData);
    }
  }, [pathString, paths]);

  function handleCloseDialog(dialogName: string) {
    dialogDispatch({ type: DIALOG_TYPES.CLOSE_DIALOG, payload: { dialogName } });
    setSelectedRow(null);
  }

  async function saveExam(name: string, grade: Grade) {
    const exam: ExamInterface = {
      id: new Date().getTime().toString(),
      objectives: {},
      name: name,
      grade: grade,
    };
    if (userId) {
      const examId = await firebaseExamService.insertExam({ userId, pathString, exam });
      openExam(examId);
    } else {
      throw new Error(`User doc does not exists for uid ${userId}.`);
    }
  }

  async function openExam(examId: string) {
    if (userId) {
      await loadExam({ userId, examId, dispatch });
      handleCloseDialog(DIALOGS.ADD_EXAM_DIALOG);
      const pathString = decodeURI(location.pathname.replace("/exam-overview", "")) || "/";
      navigate("/exam-builder" + pathString);
    }
  }

  function saveFolder(name: string) {
    if (userId) {
      firebaseExamService.insertFolder({ userId, pathString, name });
      handleCloseDialog(DIALOGS.ADD_FOLDER_DIALOG);
    } else {
      throw new Error(`User doc does not exists for uid ${userId}.`);
    }
  }

  function renameExam(name: string) {
    if (userId && selectedRow) {
      const examId = selectedRow.id;
      firebaseExamService.renameExam({ userId, pathString, examId, name });
      selectedRow.name = name;
      handleCloseDialog(DIALOGS.RENAME_EXAM_DIALOG);
    } else {
      throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
    }
  }

  function renameFolder(name: string) {
    if (userId && selectedRow) {
      const folderId = selectedRow.id;
      const prevName = selectedRow.name;
      firebaseExamService.renameFolder({ userId, pathString, folderId, prevName, newName: name });
      selectedRow.name = name;
      handleCloseDialog(DIALOGS.RENAME_FOLDER_DIALOG);
    } else {
      throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
    }
  }

  async function copyExam(newName: string) {
    if (userId && selectedRow) {
      const examId = selectedRow.id;
      const exam: ExamInterface = (await firebaseExamService.fetchExam({
        userId,
        examId,
      })) as ExamInterface;
      if (exam) {
        const newExam: ExamInterface = {
          id: new Date().getTime().toString(),
          name: newName,
          grade: exam.grade,
          objectives: exam.objectives,
        };
        firebaseExamService.insertExam({ userId, pathString, exam: newExam });
        handleCloseDialog(DIALOGS.COPY_EXAM_DIALOG);
      } else {
        throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
      }
    }
  }

  function moveExam(targetPath: string) {
    if (userId && selectedRow) {
      const examId = selectedRow.id;
      const nameExam = selectedRow.name;
      const examExists = checkItemExists(targetPath, nameExam);
      if (paths && examExists) {
        setTargetPath(targetPath);
        handleCloseDialog(DIALOGS.MOVE_EXAM_DIALOG);
        handleOpenDialog(DIALOGS.EXAM_EXISTS_DIALOG);
      } else {
        firebaseExamService.moveExam({ userId, pathString, newPathString: targetPath, examId });
        handleCloseDialog(DIALOGS.MOVE_EXAM_DIALOG);
      }
    } else {
      throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
    }
  }

  function moveFolder(targetPath: string) {
    if (userId && selectedRow) {
      const folderId = selectedRow.id;
      const nameFolder = selectedRow.name;
      const folderExists = checkItemExists(targetPath, nameFolder);
      if (folderExists) {
        setTargetPath(targetPath);
        handleCloseDialog(DIALOGS.MOVE_FOLDER_DIALOG);
        handleOpenDialog(DIALOGS.FOLDER_EXISTS_DIALOG);
      } else {
        firebaseExamService.moveFolder({ userId, pathString, newPathString: targetPath, folderId });
        handleCloseDialog(DIALOGS.MOVE_FOLDER_DIALOG);
      }
    } else {
      throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
    }
  }

  function checkItemExists(targetPath: string, nameItem: string): boolean {
    if (!paths) return false;

    const targetDirectories = paths[targetPath];
    if (!Array.isArray(targetDirectories)) return false;

    return targetDirectories.some((item) => item.name === nameItem);
  }

  function moveAndRenameExam(targetPath: string, newName: string) {
    if (userId && selectedRow) {
      const examId = selectedRow.id;
      firebaseExamService.moveAndRenameExam({ userId, pathString, newPathString: targetPath, examId, newName });
      handleCloseDialog(DIALOGS.EXAM_EXISTS_DIALOG);
    } else {
      throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
    }
  }

  function moveAndRenameFolder(targetPath: string, newName: string) {
    if (userId && selectedRow) {
      const folderId = selectedRow.id;
      firebaseExamService.moveAndRenameFolder({ userId, pathString, newPathString: targetPath, folderId, newName });
      handleCloseDialog(DIALOGS.FOLDER_EXISTS_DIALOG);
    } else {
      throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
    }
  }

  function deleteExam() {
    if (userId && selectedRow) {
      const examId = selectedRow.id;
      firebaseExamService.deleteExam({ userId, pathString, examId });
      handleCloseDialog(DIALOGS.DELETE_EXAM_DIALOG);
    } else {
      throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
    }
  }

  function deleteFolder() {
    if (userId && selectedRow) {
      const folderId = selectedRow.id;
      const name = selectedRow.name;
      firebaseExamService.deleteFolder({ userId, pathString, folderId, name });
      handleCloseDialog(DIALOGS.DELETE_FOLDER_DIALOG);
    } else {
      throw new Error(`User doc does not exists for uid ${userId} or no row selected  ${selectedRow}`);
    }
  }

  return (
    <>
      <MainCustomContainer>
        <Stack>
          <Typography variant="h2">Prüfungen Übersicht</Typography>
          <OverviewNavigation path={pathString}></OverviewNavigation>
          <SortingTable
            paths={paths}
            rows={rows}
            selected={selectedRow}
            setSelected={setSelectedRow}
            openExam={openExam}
            openDialogNewExam={handleOpenDialog}
            validGrades={validGrades}
          />
        </Stack>
        <AddExamDialog
          open={addExamDialogOpen}
          handleCancel={() => handleCloseDialog(DIALOGS.ADD_EXAM_DIALOG)}
          saveExam={saveExam}
          grades={validGrades}
          paths={paths}
          pathString={pathString}
        />
        <AddFolderDialog
          open={addFolderDialogOpen}
          handleCancel={() => handleCloseDialog(DIALOGS.ADD_FOLDER_DIALOG)}
          saveFolder={saveFolder}
          paths={paths}
          pathString={pathString}
        />
        <RenameExamDialog
          open={renameExamDialogOpen}
          renameExam={renameExam}
          handleCancel={() => handleCloseDialog(DIALOGS.RENAME_EXAM_DIALOG)}
          currentName={selectedRow?.name}
        />
        <RenameFolderDialog
          open={renameFolderDialogOpen}
          handleCancel={() => handleCloseDialog(DIALOGS.RENAME_FOLDER_DIALOG)}
          renameFolder={renameFolder}
          paths={paths}
          pathString={pathString}
          currentName={selectedRow?.name}
        />
        <CopyExamDialog
          open={copyExamDialogOpen}
          paths={paths}
          pathString={pathString}
          handleCancel={() => handleCloseDialog(DIALOGS.COPY_EXAM_DIALOG)}
          copyExam={copyExam}
        />
        <MoveExamDialog
          open={moveExamDialogOpen}
          paths={paths}
          pathString={pathString}
          handleCancel={() => handleCloseDialog(DIALOGS.MOVE_EXAM_DIALOG)}
          moveExam={moveExam}
        />
        <MoveFolderDialog
          open={moveFolderDialogOpen}
          paths={paths}
          pathString={pathString}
          handleCancel={() => handleCloseDialog(DIALOGS.MOVE_FOLDER_DIALOG)}
          moveFolder={moveFolder}
          selectedFolderName={selectedRow?.name || ""}
        />
        <ExamExistsDialog
          open={examExistsDialogOpen}
          moveAndRenameExam={moveAndRenameExam}
          handleCancel={() => handleCloseDialog(DIALOGS.EXAM_EXISTS_DIALOG)}
          targetPathString={targetPath}
        />
        <FolderExistsDialog
          open={folderExistsDialogOpen}
          moveAndRenameFolder={moveAndRenameFolder}
          handleCancel={() => handleCloseDialog(DIALOGS.FOLDER_EXISTS_DIALOG)}
          targetPathString={targetPath}
        />
        <DeleteExamDialog
          open={deleteExamDialogOpen}
          handleCancel={() => handleCloseDialog(DIALOGS.DELETE_EXAM_DIALOG)}
          deleteExam={deleteExam}
        />
        <DeleteFolderDialog
          open={deleteFolderDialogOpen}
          handleCancel={() => handleCloseDialog(DIALOGS.DELETE_FOLDER_DIALOG)}
          deleteFolder={deleteFolder}
        />
      </MainCustomContainer>
    </>
  );
};

export default ExamOverview;
